/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const ORACLE_UPDATE_DISCRIMINATOR = new Uint8Array([
  85, 209, 248, 142, 186, 249, 120, 239,
]);

export function getOracleUpdateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ORACLE_UPDATE_DISCRIMINATOR,
  );
}

export type OracleUpdateInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountOracle extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? ReadonlyAccount<TAccountPool>
        : TAccountPool,
      TAccountOracle extends string
        ? WritableAccount<TAccountOracle>
        : TAccountOracle,
      ...TRemainingAccounts,
    ]
  >;

export type OracleUpdateInstructionData = { discriminator: ReadonlyUint8Array };

export type OracleUpdateInstructionDataArgs = {};

export function getOracleUpdateInstructionDataEncoder(): FixedSizeEncoder<OracleUpdateInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: ORACLE_UPDATE_DISCRIMINATOR }),
  );
}

export function getOracleUpdateInstructionDataDecoder(): FixedSizeDecoder<OracleUpdateInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getOracleUpdateInstructionDataCodec(): FixedSizeCodec<
  OracleUpdateInstructionDataArgs,
  OracleUpdateInstructionData
> {
  return combineCodec(
    getOracleUpdateInstructionDataEncoder(),
    getOracleUpdateInstructionDataDecoder(),
  );
}

export type OracleUpdateAsyncInput<
  TAccountPool extends string = string,
  TAccountOracle extends string = string,
> = {
  pool: Address<TAccountPool>;
  oracle?: Address<TAccountOracle>;
};

export async function getOracleUpdateInstructionAsync<
  TAccountPool extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: OracleUpdateAsyncInput<TAccountPool, TAccountOracle>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  OracleUpdateInstruction<TProgramAddress, TAccountPool, TAccountOracle>
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.oracle.value) {
    accounts.oracle.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([111, 114, 97, 99, 108, 101])),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getOracleUpdateInstructionDataEncoder().encode({}),
    programAddress,
  } as OracleUpdateInstruction<TProgramAddress, TAccountPool, TAccountOracle>);
}

export type OracleUpdateInput<
  TAccountPool extends string = string,
  TAccountOracle extends string = string,
> = {
  pool: Address<TAccountPool>;
  oracle: Address<TAccountOracle>;
};

export function getOracleUpdateInstruction<
  TAccountPool extends string,
  TAccountOracle extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: OracleUpdateInput<TAccountPool, TAccountOracle>,
  config?: { programAddress?: TProgramAddress },
): OracleUpdateInstruction<TProgramAddress, TAccountPool, TAccountOracle> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('oracle', accounts.oracle),
    ],
    data: getOracleUpdateInstructionDataEncoder().encode({}),
    programAddress,
  } as OracleUpdateInstruction<TProgramAddress, TAccountPool, TAccountOracle>);
}

export type ParsedOracleUpdateInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    oracle: TAccountMetas[1];
  };
  data: OracleUpdateInstructionData;
};

export function parseOracleUpdateInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedOracleUpdateInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { pool: getNextAccount(), oracle: getNextAccount() },
    data: getOracleUpdateInstructionDataDecoder().decode(instruction.data),
  };
}
